import { t } from "@/lib/i18n/interpolate";
import {
  type Decimal,
  decimalToString,
  parseDecimal,
} from "@/lib/tool-kit/arithmetic/decimal";
import {
  floatingPointView,
  type HalfRelation,
  type IncrementRounding,
  type PlaceRounding,
  ROUNDING_MODES,
  type RoundingMode,
  roundToIncrement,
  roundToPlace,
} from "@/lib/tool-kit/arithmetic/rounding";

/** What the rounding calculator shows. */
export type RoundTarget =
  | { kind: "place"; places: number }
  | { kind: "increment"; raw: string };

export interface ModeResult {
  mode: RoundingMode;
  text: string;
}

export interface RoundingView {
  value: Decimal;
  valueText: string;
  mode: RoundingMode;
  target: RoundTarget;
  /** The selected mode's result, as text. */
  resultText: string;
  differenceText: string;
  relation: HalfRelation;
  place: PlaceRounding | null;
  increment: IncrementRounding | null;
  all: ModeResult[];
  /** True when the nearest values of the modes disagree (a tie or a direction matters). */
  modesDiffer: boolean;
  floating: { toFixed: string; exactBinary: string; differs: boolean } | null;
}

export type RoundingComputation =
  | { kind: "empty" }
  | { kind: "invalid" }
  | { kind: "invalid-increment" }
  | { kind: "ok"; view: RoundingView };

function show(d: Decimal): string {
  return decimalToString(d);
}

function signedDifference(d: Decimal): string {
  const text = decimalToString(d);
  if (text === "0") {
    return "0";
  }
  return d.negative ? text : `+${text}`;
}

function resultFor(
  value: Decimal,
  target: RoundTarget,
  mode: RoundingMode
): {
  text: string;
  place: PlaceRounding | null;
  increment: IncrementRounding | null;
} | null {
  if (target.kind === "place") {
    const place = roundToPlace(value, target.places, mode);
    return { text: show(place.result), place, increment: null };
  }
  const step = parseDecimal(target.raw);
  if (step === null) {
    return null;
  }
  const increment = roundToIncrement(value, step, mode);
  if (increment === null) {
    return null;
  }
  return { text: show(increment.result), place: null, increment };
}

export function computeRounding(
  rawValue: string,
  mode: RoundingMode,
  target: RoundTarget
): RoundingComputation {
  if (rawValue.trim() === "") {
    return { kind: "empty" };
  }
  const value = parseDecimal(rawValue);
  if (value === null) {
    return { kind: "invalid" };
  }
  const chosen = resultFor(value, target, mode);
  if (chosen === null) {
    return { kind: "invalid-increment" };
  }
  const all: ModeResult[] = [];
  for (const each of ROUNDING_MODES) {
    const row = resultFor(value, target, each);
    all.push({ mode: each, text: row?.text ?? "" });
  }
  const main = chosen.place ?? chosen.increment;
  const difference = main ? main.difference : value;
  const jsPlaces = target.kind === "place" ? target.places : null;
  const jsView = jsPlaces === null ? null : floatingPointView(value, jsPlaces);
  const half = all.find((r) => r.mode === "half-away")?.text ?? "";
  return {
    kind: "ok",
    view: {
      value,
      valueText: show(value),
      mode,
      target,
      resultText: chosen.text,
      differenceText: signedDifference(difference),
      relation: main ? main.relation : "exact",
      place: chosen.place,
      increment: chosen.increment,
      all,
      modesDiffer: new Set(all.map((r) => r.text)).size > 1,
      floating: jsView ? { ...jsView, differs: jsView.toFixed !== half } : null,
    },
  };
}

/* ------------------------------------------------------------- narration */

export interface RoundingWords {
  keep: string;
  below: string;
  tie: string;
  above: string;
  exact: string;
  stays: string;
  bumps: string;
  unchanged: string;
  incrementRatio: string;
  incrementBelow: string;
  incrementTie: string;
  incrementAbove: string;
  incrementExact: string;
  incrementResult: string;
}

function keptAndDropped(
  place: PlaceRounding,
  places: number
): { kept: string; dropped: string } {
  const digits = place.kept;
  if (places >= 0) {
    const integerLength = digits.length - places;
    const keptText =
      places > 0
        ? `${digits.slice(0, integerLength)}.${digits.slice(integerLength)}`
        : digits;
    return { kept: keptText, dropped: place.dropped };
  }
  return { kept: digits + "0".repeat(-places), dropped: place.dropped };
}

function relationLine(
  relation: HalfRelation,
  templates: Record<HalfRelation, string>,
  values: Record<string, string>
): string {
  return t(templates[relation], values);
}

export function roundingLines(
  view: RoundingView,
  words: RoundingWords,
  unitText: string
): string[] {
  const { place, increment } = view;
  if (place) {
    if (place.unchanged) {
      return [t(words.unchanged, { value: view.valueText, unit: unitText })];
    }
    const places = view.target.kind === "place" ? view.target.places : 0;
    const split = keptAndDropped(place, places);
    const values = {
      kept: split.kept,
      dropped: split.dropped,
      half: `5${"0".repeat(Math.max(split.dropped.length - 1, 0))}`,
      unit: unitText,
      result: view.resultText,
    };
    const first = t(words.keep, values);
    const second = relationLine(
      place.relation,
      {
        below: words.below,
        tie: words.tie,
        above: words.above,
        exact: words.exact,
      },
      values
    );
    const keptValue = split.kept.replace("−", "");
    const resultMagnitude = view.resultText.replace("−", "");
    const goesUp = Number(resultMagnitude) > Number(keptValue);
    return [first, second, t(goesUp ? words.bumps : words.stays, values)];
  }
  if (increment) {
    const values = {
      value: view.valueText,
      step: view.target.kind === "increment" ? view.target.raw.trim() : "",
      ratio: increment.ratio,
      multiple: increment.multiple.toString().replace("-", "−"),
      result: view.resultText,
    };
    const relation = relationLine(
      increment.relation,
      {
        below: words.incrementBelow,
        tie: words.incrementTie,
        above: words.incrementAbove,
        exact: words.incrementExact,
      },
      values
    );
    return [
      t(words.incrementRatio, values),
      relation,
      t(words.incrementResult, values),
    ];
  }
  return [];
}
